import { useReducer, useState } from 'react';
import type { UndefinedOnPartialDeep } from 'type-fest';

import { translate } from '@repo/phrase';
import { assetTypes } from '@repo/types/asset';
import type { Service } from '@repo/types/service';
import { Paragraph } from '@repo/ui/typography';
import { isActionKey } from '@repo/utils';
import { hasItems } from '@repo/utils/type.utils';

import { ArticleListModal } from '@/components/services-table/article-list-modal';
import { ServiceTimeWindow } from '@/components/services-table/service-time-window';
import { ServicesTitle } from '@/components/services-table/services-title';

import { useAssetParams } from '@/hooks/asset.hooks';
import { formatServiceStatus, getServiceUniqueKey } from '@/utils/services.utils';
import { formatPrice, getServicePaymentTranslation } from '@/utils/translation.utils';
import { getAppConfig } from '@/utils/window.utils';

import styles from './styles.module.scss';

interface Props {
    services: UndefinedOnPartialDeep<Service[]> | undefined;
}

const tableHeader = [
    { key: crypto.randomUUID(), text: 'services_table_name' },
    { key: crypto.randomUUID(), text: 'services_table_detail' },
    { key: crypto.randomUUID(), text: 'services_table_provider' },
    { key: crypto.randomUUID(), text: 'services_table_pay' },
    { key: crypto.randomUUID(), text: 'services_table_status' },
    { key: crypto.randomUUID(), text: 'services_table_price' },
];

export const ServicesTable = ({ services }: Props) => {
    const { assetType } = useAssetParams();

    const config = getAppConfig();
    const isOrder = assetType === assetTypes.order;
    const [isModalOpen, toggleModal] = useReducer((prev) => !prev, false);
    const [selectedServiceKey, setSelectedServiceKey] = useState<string>();

    const hasServices = hasItems(services);
    if (!hasServices) {
        return null;
    }

    const selectedService = services.find((service) => getServiceUniqueKey(service) === selectedServiceKey);

    const openArticleModal = (data: UndefinedOnPartialDeep<Service>) => {
        setSelectedServiceKey(getServiceUniqueKey(data));
        toggleModal();
    };

    const clearSelectedService = () => {
        setSelectedServiceKey(undefined);
    };

    const enablePriceExclTax = config.feature.priceExclTax?.enable;

    const totalPrice = services
        .map((item) => (enablePriceExclTax ? item.price?.priceExclTax : item.price?.priceInclTax))
        .reduce((acc: number, curr) => acc + (curr ?? 0), 0);
    const currency = services[0]?.price?.currencyCode;

    return (
        <div data-testid="services-table" className={styles.services_table}>
            <ServicesTitle numberOfServices={services.length} totalPrice={totalPrice} currency={currency} />
            <div className={styles.table}>
                <div className={styles.services_table_header}>
                    {tableHeader.map((headerItem) => (
                        <div key={headerItem.key}>
                            <Paragraph bodySize="s">{translate(headerItem.text)}</Paragraph>
                        </div>
                    ))}
                </div>
                {services.map((service, key) => {
                    const serviceProviderPreferredPayment = getServicePaymentTranslation(
                        service.servicePaymentTo,
                        isOrder
                    );
                    const price = formatPrice(
                        enablePriceExclTax ? service.price?.priceExclTax : service.price?.priceInclTax,
                        service.price?.currencyCode
                    );
                    const serviceProviderName = !isOrder ? '' : service.serviceProviderName;
                    const serviceStatusText =
                        isOrder && service.isDelivery
                            ? translate(
                                  `services_article_delivery_view_delivery_status_${formatServiceStatus(service.status)}`
                              )
                            : service.statusText;

                    return (
                        <div
                            key={getServiceUniqueKey(service)}
                            className={styles.services_table_row}
                            data-testid={`services-table-list-${key}`}
                            onClick={() => {
                                openArticleModal(service);
                            }}
                            onKeyDown={(event) => {
                                isActionKey(event.key) && openArticleModal(service);
                            }}
                            // oxlint-disable-next-line jsx-a11y/prefer-tag-over-role
                            role="button"
                            tabIndex={0}
                        >
                            <div>
                                <Paragraph>{service.serviceName}</Paragraph>
                            </div>
                            <div>
                                <ServiceTimeWindow service={service} isOrder={isOrder} />
                            </div>
                            <div>
                                <Paragraph>{serviceProviderName}</Paragraph>
                            </div>
                            <div>
                                <Paragraph>{serviceProviderPreferredPayment}</Paragraph>
                            </div>
                            <div>
                                <Paragraph>{serviceStatusText}</Paragraph>
                            </div>
                            <div>
                                <Paragraph>{price}</Paragraph>
                            </div>
                        </div>
                    );
                })}
            </div>
            <ArticleListModal
                visible={isModalOpen}
                toggleVisible={toggleModal}
                service={selectedService}
                onClose={clearSelectedService}
            />
        </div>
    );
};
